import { useEffect, useRef } from 'react';
import './neural-background.css';

/**
 * NeuralBackground
 *
 * A full-viewport, theme-aware animated "neural network / graph" that sits
 * BEHIND all page content. It is purely decorative: `fixed inset-0`, `z-0`,
 * `pointer-events-none`, `aria-hidden`.
 *
 * Layers stack here (back to front): (1) a theme-aware base gradient, (2) three
 * large blurred glow orbs that slowly DRIFT and PULSE (breathe) plus a faint
 * masked grid — together reproducing the "glow and pulse" ambience of the
 * KI-Briefing deck — (3) the animated <canvas> network, and (4) a soft vignette
 * on top to focus the centre. See neural-background.css for the CSS layers.
 *
 * Rendering is done on a raw <canvas> driven by requestAnimationFrame (no
 * framer-motion). Nodes drift slowly, bouncing at the edges; lines are drawn
 * between nodes within a distance threshold and fade with distance, so the
 * structure continuously forms and breaks. Faint pulses travel along some
 * edges, and nodes near the cursor light up and connect a little further.
 *
 * Theme awareness: the palette is read from the `dark` class on <html> and a
 * MutationObserver keeps it in sync when the user toggles the theme. Dark uses
 * warm orange/amber nodes + lines with cyan accents; light uses blue/teal.
 *
 * Performance: O(n^2) over <= 70 nodes, the loop is cancelled on unmount and
 * paused while the tab is hidden. devicePixelRatio is honoured for crispness
 * and the canvas is re-sized on window resize. prefers-reduced-motion does NOT
 * stop the animation (the calm drift is intentional) — it only slows it.
 */

interface NeuralBackgroundProps {
  /** Extra classes appended to the fixed background layer. */
  className?: string;
}

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Base glow radius in CSS pixels. */
  radius: number;
  /** Per-node phase offset so dots "twinkle" out of sync with the breathe. */
  tw: number;
}

interface Pulse {
  /** Index of the edge endpoints. */
  a: number;
  b: number;
  /** Progress along the edge, 0..1. */
  t: number;
  /** Speed in progress-per-second. */
  speed: number;
}

interface Palette {
  node: string;
  nodeAccent: string;
  /** RGB triple for line colour, used with a computed alpha. */
  lineRgb: string;
  accentRgb: string;
  /** Overall opacity multiplier — bolder on dark, gentler on light. */
  intensity: number;
  /**
   * Multiplier for the soft node halo (canvas shadowBlur + shadow alpha).
   * On a DARK backdrop a wide additive-looking halo reads as a glow; on a
   * WHITE backdrop the same shadow turns into a muddy grey smudge, so light
   * mode dials it right down and relies on crisp, saturated dots instead.
   */
  glowScale: number;
  /**
   * Multiplier for the wide "bloom underline" stroke under each link. Kept
   * full on dark (the filament glow), reduced on light (avoids grey haze).
   */
  bloomScale: number;
}

const DARK_PALETTE: Palette = {
  node: '#e2e8f0', // slate-200, luminous near-white
  nodeAccent: '#f97316', // orange-500 hover accent (warm brand colour)
  lineRgb: '226, 232, 240', // slate-200 — crisp, bright lines
  accentRgb: '249, 115, 22', // orange-500
  intensity: 1.6, // was 2.6 — reduced so lines/dots sit behind text
  glowScale: 0.9, // was 1.4 — less halo for better text readability
  bloomScale: 0.3, // was 0.4
};

const LIGHT_PALETTE: Palette = {
  node: '#2563eb', // blue-600 (saturated so it reads on white)
  nodeAccent: '#0d9488', // teal-600
  lineRgb: '37, 99, 235', // blue-600
  accentRgb: '13, 148, 136', // teal-600
  // On the airy white backdrop the saturated lines/dots already read clearly,
  // so the overall intensity is a touch gentler and the halo/bloom are damped
  // (see glowScale/bloomScale) to keep the look crisp instead of smudgy.
  intensity: 1.7,
  glowScale: 0.5,
  bloomScale: 0.5,
};

function readPalette(): Palette {
  if (typeof document === 'undefined') return DARK_PALETTE;
  return document.documentElement.classList.contains('dark')
    ? DARK_PALETTE
    : LIGHT_PALETTE;
}

export function NeuralBackground({ className }: NeuralBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const frameRef = useRef<number>(0);

  useEffect(() => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;
    const canvas: HTMLCanvasElement = canvasEl;

    const ctx = canvas.getContext('2d');
    // Guard every ctx use so the component still renders if 2d is unavailable
    // (e.g. jsdom). Without a context there is nothing to animate.
    if (!ctx) return;
    const context: CanvasRenderingContext2D = ctx;

    let width = 0;
    let height = 0;
    let dpr = 1;

    let nodes: Node[] = [];
    let pulses: Pulse[] = [];
    let palette = readPalette();

    // Slow the calm drift further (but never stop it) when reduced motion is
    // requested — the visible movement is intentional per design.
    const prefersReduced =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;
    const speedScale = prefersReduced ? 0.45 : 1;

    // Mouse position in CSS pixels (-1, -1 means "off-canvas").
    const mouse = { x: -1, y: -1 };

    const rand = (min: number, max: number) => min + Math.random() * (max - min);

    function nodeCountFor(w: number): number {
      const lowDpr = dpr < 1.5;
      if (w < 640) return lowDpr ? 50 : 65;
      if (w < 1024) return 95;
      return lowDpr ? 120 : 160;
    }

    function buildNodes() {
      const count = nodeCountFor(width);
      nodes = [];
      for (let i = 0; i < count; i++) {
        nodes.push({
          x: rand(0, width),
          y: rand(0, height),
          // Calm velocities in CSS px/sec.
          vx: rand(-9, 9),
          vy: rand(-9, 9),
          radius: rand(2.0, 4.2),
          tw: rand(0, Math.PI * 2),
        });
      }
      pulses = [];
    }

    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      // Draw in CSS-pixel coordinates; the transform handles crispness.
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildNodes();
    }

    // Connection distance threshold (CSS px), scaled to viewport size.
    const baseThreshold = () => Math.min(220, Math.max(140, width / 7.5));

    function spawnPulse() {
      if (nodes.length < 2) return;
      const a = Math.floor(Math.random() * nodes.length);
      let b = Math.floor(Math.random() * nodes.length);
      if (b === a) b = (b + 1) % nodes.length;
      pulses.push({ a, b, t: 0, speed: rand(0.28, 0.6) * speedScale });
    }

    let lastTime = performance.now();
    let pulseTimer = 0;
    // Synchronised "breathing" driver: the whole network brightens and dims
    // together on a slow ~6s cycle, exactly like the deck's particle net. This
    // is the "glowing and pulsating" effect.
    let breathePhase = 0;

    function step(now: number) {
      const dt = Math.min(0.05, (now - lastTime) / 1000); // clamp big gaps
      lastTime = now;

      const threshold = baseThreshold();
      const mouseRadius = 160;

      // Slow global breathe: pulse 0..1 over ~6s, brightness oscillates 0.6..1.
      breathePhase += dt * speedScale;
      const pulse = 0.5 + 0.5 * Math.sin(breathePhase * 1.05);
      const breathe = 0.6 + pulse * 0.4;

      // --- Update node positions (gentle drift, bounce at edges). ---
      for (const n of nodes) {
        n.x += n.vx * speedScale * dt;
        n.y += n.vy * speedScale * dt;
        if (n.x < 0) {
          n.x = 0;
          n.vx = Math.abs(n.vx);
        } else if (n.x > width) {
          n.x = width;
          n.vx = -Math.abs(n.vx);
        }
        if (n.y < 0) {
          n.y = 0;
          n.vy = Math.abs(n.vy);
        } else if (n.y > height) {
          n.y = height;
          n.vy = -Math.abs(n.vy);
        }
      }

      // --- Manage edge pulses. ---
      pulseTimer += dt;
      const pulseInterval = prefersReduced ? 2.4 : 1.4;
      if (pulseTimer > pulseInterval && pulses.length < 12) {
        pulseTimer = 0;
        spawnPulse();
      }
      for (let i = pulses.length - 1; i >= 0; i--) {
        pulses[i].t += pulses[i].speed * dt;
        if (pulses[i].t >= 1) pulses.splice(i, 1);
      }

      // --- Draw. ---
      context.clearRect(0, 0, width, height);

      context.lineWidth = 1;

      // Polygon fills: faint triangle between every connected triplet — the
      // plexus polygon effect visible in the reference image. Capped at triCap
      // nodes so this stays O(triCap² × avg-neighbours) not O(n³).
      const triCap = Math.min(nodes.length, 80);
      for (let i = 0; i < triCap; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < triCap; j++) {
          const b = nodes[j];
          const dxAB = a.x - b.x; const dyAB = a.y - b.y;
          const dABSq = dxAB * dxAB + dyAB * dyAB;
          if (dABSq > threshold * threshold) continue;
          const dAB = Math.sqrt(dABSq);
          for (let k = j + 1; k < triCap; k++) {
            const c = nodes[k];
            const dxAC = a.x - c.x; const dyAC = a.y - c.y;
            const dACSq = dxAC * dxAC + dyAC * dyAC;
            if (dACSq > threshold * threshold) continue;
            const dxBC = b.x - c.x; const dyBC = b.y - c.y;
            const dBCSq = dxBC * dxBC + dyBC * dyBC;
            if (dBCSq > threshold * threshold) continue;
            const dAC = Math.sqrt(dACSq);
            const dBC = Math.sqrt(dBCSq);
            const minFade = Math.min(1 - dAB / threshold, 1 - dAC / threshold, 1 - dBC / threshold);
            if (minFade <= 0) continue;
            const useAccent = (i + j * 3 + k * 7) % 11 === 0;
            const rgb = useAccent ? palette.accentRgb : palette.lineRgb;
            context.fillStyle = `rgba(${rgb}, ${(minFade * 0.045 * palette.intensity * breathe).toFixed(3)})`;
            context.beginPath();
            context.moveTo(a.x, a.y);
            context.lineTo(b.x, b.y);
            context.lineTo(c.x, c.y);
            context.closePath();
            context.fill();
          }
        }
      }

      // Lines between nearby nodes. O(n^2), n <= 160 is cheap.
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const distSq = dx * dx + dy * dy;

          // Cursor extends the reach a little for nodes near the pointer.
          let reach = threshold;
          if (mouse.x >= 0) {
            const mAx = a.x - mouse.x;
            const mAy = a.y - mouse.y;
            if (mAx * mAx + mAy * mAy < mouseRadius * mouseRadius) {
              reach = threshold * 1.4;
            }
          }
          if (distSq > reach * reach) continue;

          const dist = Math.sqrt(distSq);
          // Closer = brighter; the whole net breathes via `breathe`.
          const fade = 1 - dist / reach;
          const alpha = Math.min(0.72, fade * 0.46 * palette.intensity * breathe);
          // Occasionally tint a line with the accent colour for variety.
          const useAccent = (i * 31 + j * 17) % 7 === 0;
          const rgb = useAccent ? palette.accentRgb : palette.lineRgb;
          // Soft bloom underline (wide, faint) + crisp line on top — the deck's
          // two-pass stroke that makes the links read as glowing filaments. The
          // bloom is damped on light so it reads crisp instead of grey-smudgy.
          context.strokeStyle = `rgba(${rgb}, ${(alpha * 0.32 * palette.bloomScale).toFixed(3)})`;
          context.lineWidth = 3;
          context.beginPath();
          context.moveTo(a.x, a.y);
          context.lineTo(b.x, b.y);
          context.stroke();
          context.strokeStyle = `rgba(${rgb}, ${alpha.toFixed(3)})`;
          context.lineWidth = 1;
          context.beginPath();
          context.moveTo(a.x, a.y);
          context.lineTo(b.x, b.y);
          context.stroke();
        }
      }
      context.lineWidth = 1;

      // Pulses travelling along edges.
      for (const p of pulses) {
        const a = nodes[p.a];
        const b = nodes[p.b];
        if (!a || !b) continue;
        const px = a.x + (b.x - a.x) * p.t;
        const py = a.y + (b.y - a.y) * p.t;
        // Fade in then out across the edge.
        const env = Math.sin(p.t * Math.PI);
        const alpha = Math.min(1, env * 0.5 * palette.intensity);
        context.fillStyle = `rgba(${palette.accentRgb}, ${alpha.toFixed(3)})`;
        context.beginPath();
        context.arc(px, py, 1.6, 0, Math.PI * 2);
        context.fill();
      }

      // Nodes as glowing dots that breathe: a real canvas shadow gives each a
      // soft halo whose blur + brightness pulse with the network (deck style).
      // The halo is scaled by the theme's glowScale so light mode stays crisp.
      context.save();
      context.shadowBlur = (10 + pulse * 20) * palette.glowScale;
      for (const n of nodes) {
        // Per-node twinkle: a small out-of-phase shimmer on top of the global
        // breathe, so the field feels alive rather than uniformly pulsing.
        const tw = 0.82 + 0.18 * Math.sin(breathePhase * 1.8 + n.tw);
        let r = n.radius * tw;
        let lit = false;
        if (mouse.x >= 0) {
          const mdx = n.x - mouse.x;
          const mdy = n.y - mouse.y;
          if (mdx * mdx + mdy * mdy < mouseRadius * mouseRadius) {
            r = n.radius * 1.9;
            lit = true;
          }
        }
        const rgb = lit ? palette.accentRgb : palette.lineRgb;
        const base = lit ? 0.95 : (0.6 + pulse * 0.3) * tw;
        const a = Math.min(1, base * palette.intensity);
        context.shadowColor = `rgba(${rgb}, ${((0.55 + pulse * 0.4) * palette.glowScale).toFixed(3)})`;
        context.fillStyle = `rgba(${rgb}, ${a.toFixed(3)})`;
        context.beginPath();
        context.arc(n.x, n.y, r, 0, Math.PI * 2);
        context.fill();
      }
      context.restore();

      frameRef.current = window.requestAnimationFrame(step);
    }

    function start() {
      lastTime = performance.now();
      frameRef.current = window.requestAnimationFrame(step);
    }

    function stop() {
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = 0;
      }
    }

    function onVisibility() {
      if (document.hidden) {
        stop();
      } else if (!frameRef.current) {
        start();
      }
    }

    function onPointerMove(e: PointerEvent) {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    }

    function onPointerLeave() {
      mouse.x = -1;
      mouse.y = -1;
    }

    const themeObserver = new MutationObserver(() => {
      palette = readPalette();
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    resize();
    start();

    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', onVisibility);
    // Listen on window (the canvas itself is pointer-events-none).
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerleave', onPointerLeave);

    return () => {
      stop();
      themeObserver.disconnect();
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerleave', onPointerLeave);
    };
  }, []);

  const layerClass = [
    'neural-bg fixed inset-0 z-0 pointer-events-none overflow-hidden',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={layerClass} aria-hidden="true">
      {/* Theme-aware base gradient (deep-space tint on dark, airy cool tint on
          light) — gives the network depth to glow against. */}
      <div className="neural-bg__base" />
      {/* Ambient glow orbs + grid (CSS-only, drift + pulse) behind the canvas. */}
      <div className="neural-bg__orb neural-bg__orb--a" />
      <div className="neural-bg__orb neural-bg__orb--b" />
      <div className="neural-bg__orb neural-bg__orb--c" />
      <canvas ref={canvasRef} className="neural-bg__canvas block h-full w-full" />
      {/* Vignette drawn on top of the canvas to focus the centre (deck motif). */}
      <div className="neural-bg__vignette" />
    </div>
  );
}

export default NeuralBackground;
